import {
  PRODUCT_PAGE_UPSELL_TEMPLATE_TYPE,
  STATIC_PRODUCT_PAGE_UPSELL_SECTION_COUNT,
  STATIC_PRODUCT_PAGE_UPSELL_SECTION_INDEX,
  isStaticProductPageUpsellContentStep,
} from "./product-page-upsell-schema.js";
import { POPUP_LAYOUT_IDS, isPopupLayout } from "./popup-layouts/index.js";
import { PRODUCT_SHELL_LAYOUT } from "./product-shell-layout.js";
import {
  DEFAULT_VARIANT_PICKER_DISPLAY,
  VARIANT_PICKER_STYLE,
} from "./product-block-display.js";
import {
  buildCartDrawerHorizontalProductSubsections,
  buildCartDrawerVerticalProductSubsections,
  CART_DRAWER_UPSELL_ITEMS_PER_ROW_DEFAULT,
  CART_DRAWER_UPSELL_ITEMS_PER_ROW_MAX,
  CART_DRAWER_UPSELL_ITEMS_PER_ROW_MIN,
  CART_DRAWER_UPSELL_LAYOUT_OPTIONS,
  clampCartDrawerUpsellItemsPerRow,
  isCartDrawerUpsellVerticalShell,
} from "./custom-cart-drawer-template.js";
import { isCartDrawerCheckboxStackShell, isCartDrawerFbtBundleShell } from "./product-shell-layout.js";
import { setSectionVisibility } from "./section-visibility.js";
import { buildProductRecommendationBrandAttributionSection } from "./product-recommendation-popup-schema.js";
import {
  PRODUCT_UPSELL_BUY_NOW_CTA_ACTION,
  PRODUCT_UPSELL_BUY_NOW_CTA_ROLE,
  PRODUCT_UPSELL_BUY_NOW_CTA_TEXT,
  PRODUCT_UPSELL_PRIMARY_CTA_ACTION,
} from "./product-upsell-recommendation-defaults.js";
import { buildDefaultProductPageSlotEmbed } from "./product-page-slot.js";

export {
  PRODUCT_PAGE_UPSELL_TEMPLATE_TYPE,
  STATIC_PRODUCT_PAGE_UPSELL_SECTION_COUNT,
  STATIC_PRODUCT_PAGE_UPSELL_SECTION_INDEX,
  isStaticProductPageUpsellContentStep,
};

/** Re-export cart drawer upsell layout picker — horizontal row + carousel cards. */
export {
  CART_DRAWER_UPSELL_ITEMS_PER_ROW_MIN as PRODUCT_PAGE_UPSELL_ITEMS_PER_ROW_MIN,
  CART_DRAWER_UPSELL_ITEMS_PER_ROW_MAX as PRODUCT_PAGE_UPSELL_ITEMS_PER_ROW_MAX,
  CART_DRAWER_UPSELL_ITEMS_PER_ROW_DEFAULT as PRODUCT_PAGE_UPSELL_ITEMS_PER_ROW_DEFAULT,
  clampCartDrawerUpsellItemsPerRow as clampProductPageUpsellItemsPerRow,
};

/** Default columns when carousel is shown as a grid. */
export const PRODUCT_PAGE_UPSELL_GRID_ITEMS_PER_ROW_DEFAULT = 5;

export const PRODUCT_PAGE_UPSELL_PRODUCTS_SHOWN_AT_ONCE_MIN = 1;
export const PRODUCT_PAGE_UPSELL_PRODUCTS_SHOWN_AT_ONCE_MAX = 4;
export const PRODUCT_PAGE_UPSELL_PRODUCTS_SHOWN_AT_ONCE_DEFAULT = 1;
export const PRODUCT_PAGE_UPSELL_PRODUCTS_SHOWN_AT_ONCE_FBT_DEFAULT = 3;

export function clampProductPageUpsellProductsShownAtOnce(
  value,
  fallback = PRODUCT_PAGE_UPSELL_PRODUCTS_SHOWN_AT_ONCE_DEFAULT
) {
  const raw = Number(value);
  if (!Number.isFinite(raw)) return fallback;
  return Math.min(
    PRODUCT_PAGE_UPSELL_PRODUCTS_SHOWN_AT_ONCE_MAX,
    Math.max(PRODUCT_PAGE_UPSELL_PRODUCTS_SHOWN_AT_ONCE_MIN, Math.round(raw))
  );
}

/** Product page upsell layout picker */
export const PRODUCT_PAGE_UPSELL_LAYOUT_OPTIONS = Object.freeze([
  ...CART_DRAWER_UPSELL_LAYOUT_OPTIONS,
  {
    value: PRODUCT_SHELL_LAYOUT.CART_DRAWER_CHECKBOX_STACK,
    label: "Product Add-Ons Upsell",
    description: "Add discounted extras that go well with the main item",
    preview: "cart-upsell-checkbox-stack",
  },
  {
    value: PRODUCT_SHELL_LAYOUT.CART_DRAWER_FBT_BUNDLE,
    label: "Frequently Bought Together Upsell",
    description: "Bundle products with related items - all discounted together",
    preview: "cart-upsell-fbt-bundle",
  },
]);

/** Product Add-Ons layout hides the per-row + Add button — checkboxes replace it. */
export function applyProductPageCheckboxAddButtonHidden(subsections) {
  if (!Array.isArray(subsections)) return subsections;
  return subsections.map((sub) => {
    if (sub?.type !== "BUTTON") return sub;
    const next = JSON.parse(JSON.stringify(sub));
    setSectionVisibility(next, false, "flex");
    return next;
  });
}

/** @deprecated Use applyProductPageCheckboxAddButtonHidden */
export const applyProductPageFbtAddButtonHidden = applyProductPageCheckboxAddButtonHidden;

/** FBT keeps the button visible for the bottom Claim Offer CTA (styled via editor). */
export function applyProductPageFbtClaimButtonVisible(subsections) {
  if (!Array.isArray(subsections)) return subsections;
  return subsections.map((sub) => {
    if (sub?.type !== "BUTTON") return sub;
    const next = JSON.parse(JSON.stringify(sub));
    setSectionVisibility(next, true, "flex");
    next.text = "Claim Offer";
    return next;
  });
}

function buildProductPageUpsellVariantPickerSubsection() {
  return {
    type: "VARIANT_PICKER",
    search: { placeholder: "Search options…" },
    setting: {
      display: {
        ...DEFAULT_VARIANT_PICKER_DISPLAY,
        variantPickerVariant: VARIANT_PICKER_STYLE.PREMIUM_DROPDOWN,
        label: "",
      },
      variantPickerVariant: VARIANT_PICKER_STYLE.PREMIUM_DROPDOWN,
      customCSS: {
        desktop: { fontSize: "12px", fontWeight: "500", color: "#6b7280" },
        mobile: { fontSize: "11px", fontWeight: "500", color: "#6b7280" },
      },
      overrideCss: { desktop: "", mobile: "" },
    },
  };
}

function withProductPageVariantPicker(subsections) {
  const buttonIndex = subsections.findIndex((sub) => sub?.type === "BUTTON");
  if (buttonIndex < 0) {
    return [...subsections, buildProductPageUpsellVariantPickerSubsection()];
  }
  return [
    ...subsections.slice(0, buttonIndex),
    buildProductPageUpsellVariantPickerSubsection(),
    ...subsections.slice(buttonIndex),
  ];
}

export function buildProductPageUpsellHorizontalProductSubsections() {
  return applyProductPageUpsellTypographyDefaults(
    withProductPageVariantPicker(buildCartDrawerHorizontalProductSubsections())
  );
}

export function buildProductPageUpsellVerticalProductSubsections() {
  const sections = applyProductPageUpsellTypographyDefaults(
    withProductPageVariantPicker(buildCartDrawerVerticalProductSubsections())
  );
  ensureProductPageCarouselBuyNowButton({ sections });
  return sections;
}

/**
 * Shared product-page typography for every upsell layout (horizontal, carousel,
 * add-ons, FBT). Keeps Design title/price/compare sizes consistent on layout switch.
 */
export function applyProductPageUpsellTypographyDefaults(sections) {
  if (!Array.isArray(sections)) return sections;

  const priceOverride = `.price-container{
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 6px;
  margin: 0;
  padding: 0;
}
.price {
  font-weight: 700;
  font-size: 14px;
  color: #111827;
  line-height: 1.2;
}
.compare-price {
  text-decoration: line-through;
  color: #6b7280;
  font-size: 12px;
  line-height: 1.2;
}`;

  return sections.map((sub) => {
    if (!sub || typeof sub !== "object") return sub;

    if (sub.type === "TITLE") {
      const next = JSON.parse(JSON.stringify(sub));
      next.setting = next.setting || {};
      next.setting.customCSS = next.setting.customCSS || {};
      for (const viewport of ["desktop", "mobile"]) {
        next.setting.customCSS[viewport] = {
          ...(next.setting.customCSS[viewport] || {}),
          fontSize: "14px",
        };
      }
      return next;
    }

    if (sub.type === "PRICE") {
      const next = JSON.parse(JSON.stringify(sub));
      next.setting = next.setting || {};
      next.setting.customCSS = next.setting.customCSS || {};
      for (const viewport of ["desktop", "mobile"]) {
        next.setting.customCSS[viewport] = {
          ...(next.setting.customCSS[viewport] || {}),
          fontSize: "14px",
        };
      }
      next.setting.display = {
        ...(next.setting.display || {}),
        compareAtFontSize: "12px",
      };
      next.setting.overrideCss = {
        desktop: priceOverride,
        mobile: priceOverride,
      };
      return next;
    }

    return sub;
  });
}

/**
 * Ensure Carousel cards PRODUCT section has a Buy Now CTA (checkout) after Add.
 * Idempotent — safe for existing campaigns that only have + Add.
 * Product page only — do not use on custom cart drawer upsell.
 * @param {object | null | undefined} productSection
 */
export function ensureProductPageCarouselBuyNowButton(productSection) {
  if (!productSection || !Array.isArray(productSection.sections)) return productSection;

  const isBuyNowButton = (sub) =>
    sub?.type === "BUTTON" &&
    (sub?.setting?.role === PRODUCT_UPSELL_BUY_NOW_CTA_ROLE ||
      sub?.setting?.action?.parameters?.event === "product_check_out");

  const existing = productSection.sections.find(isBuyNowButton);
  if (existing) {
    existing.setting = existing.setting || {};
    existing.setting.role = PRODUCT_UPSELL_BUY_NOW_CTA_ROLE;
    if (!existing.setting.action?.parameters?.event) {
      existing.setting.action = {
        ...PRODUCT_UPSELL_BUY_NOW_CTA_ACTION,
        parameters: { ...PRODUCT_UPSELL_BUY_NOW_CTA_ACTION.parameters },
      };
    } else if (!existing.setting.action.parameters.buyNowAction) {
      existing.setting.action.parameters = {
        ...existing.setting.action.parameters,
        buyNowAction: PRODUCT_UPSELL_BUY_NOW_CTA_ACTION.parameters.buyNowAction,
      };
    }
    return productSection;
  }

  const primaryIndex = productSection.sections.findIndex(
    (sub) =>
      sub?.type === "BUTTON" &&
      sub?.setting?.role !== PRODUCT_UPSELL_BUY_NOW_CTA_ROLE
  );
  const primary =
    primaryIndex >= 0 ? productSection.sections[primaryIndex] : null;
  const css = primary?.setting?.customCSS
    ? JSON.parse(JSON.stringify(primary.setting.customCSS))
    : {
        desktop: {
          backgroundColor: "#0a0e1a",
          color: "#ffffff",
          borderRadius: 8,
          fontSize: "11px",
          fontWeight: "700",
          width: "100%",
          minWidth: "0",
          paddingLeft: "10px",
          paddingRight: "10px",
        },
        mobile: {
          backgroundColor: "#0a0e1a",
          color: "#ffffff",
          borderRadius: 8,
          fontSize: "11px",
          fontWeight: "700",
          width: "100%",
          minWidth: "0",
          paddingLeft: "10px",
          paddingRight: "10px",
        },
      };

  const buyNow = {
    type: "BUTTON",
    text: PRODUCT_UPSELL_BUY_NOW_CTA_TEXT,
    setting: {
      role: PRODUCT_UPSELL_BUY_NOW_CTA_ROLE,
      action: {
        ...PRODUCT_UPSELL_BUY_NOW_CTA_ACTION,
        parameters: { ...PRODUCT_UPSELL_BUY_NOW_CTA_ACTION.parameters },
      },
      isConversationEnabled: true,
      btn_type: { type: "text" },
      customCSS: css,
      overrideCss: { desktop: "", mobile: "" },
    },
  };

  if (primaryIndex >= 0) {
    productSection.sections.splice(primaryIndex + 1, 0, buyNow);
  } else {
    productSection.sections.push(buyNow);
  }
  return productSection;
}

/**
 * Remove Buy Now CTAs from cart drawer upsell cards (product-page-only feature).
 * @param {object | null | undefined} productSection
 */
export function stripBuyNowCtaFromProductSection(productSection) {
  if (!productSection || !Array.isArray(productSection.sections)) {
    return productSection;
  }
  productSection.sections = productSection.sections.filter(
    (sub) =>
      !(
        sub?.type === "BUTTON" &&
        (sub?.setting?.role === PRODUCT_UPSELL_BUY_NOW_CTA_ROLE ||
          sub?.setting?.action?.parameters?.event === "product_check_out")
      )
  );
  return productSection;
}

/** Swap subsection defaults when merchant changes upsell layout in product page editor. */
export function buildProductPageUpsellSubsectionsForShell(shell) {
  const normalized = shell === PRODUCT_SHELL_LAYOUT.HORIZONTAL
    ? PRODUCT_SHELL_LAYOUT.CART_DRAWER_HORIZONTAL
    : shell;
  if (isCartDrawerCheckboxStackShell(normalized)) {
    return applyProductPageCheckboxAddButtonHidden(
      buildProductPageUpsellHorizontalProductSubsections()
    );
  }
  if (isCartDrawerFbtBundleShell(normalized)) {
    return applyProductPageFbtClaimButtonVisible(
      buildProductPageUpsellHorizontalProductSubsections()
    );
  }
  return isCartDrawerUpsellVerticalShell(normalized)
    ? buildProductPageUpsellVerticalProductSubsections()
    : buildProductPageUpsellHorizontalProductSubsections();
}

export function buildProductPageUpsellLayoutFields(shell, existingLayout = {}) {
  const normalized = shell === PRODUCT_SHELL_LAYOUT.HORIZONTAL
    ? PRODUCT_SHELL_LAYOUT.CART_DRAWER_HORIZONTAL
    : shell;
  const isVertical = isCartDrawerUpsellVerticalShell(normalized);
  const isCheckboxStack = isCartDrawerCheckboxStackShell(normalized);
  const isFbtBundle = isCartDrawerFbtBundleShell(normalized);
  const showAsGrid = isVertical && existingLayout?.showAsGrid === true;
  const itemsPerRow = clampCartDrawerUpsellItemsPerRow(
    existingLayout?.itemsPerRow,
    showAsGrid
      ? PRODUCT_PAGE_UPSELL_GRID_ITEMS_PER_ROW_DEFAULT
      : CART_DRAWER_UPSELL_ITEMS_PER_ROW_DEFAULT
  );
  const productsShownAtOnce = clampProductPageUpsellProductsShownAtOnce(
    existingLayout?.productsShownAtOnce,
    isCheckboxStack
      ? PRODUCT_PAGE_UPSELL_PRODUCTS_SHOWN_AT_ONCE_FBT_DEFAULT
      : PRODUCT_PAGE_UPSELL_PRODUCTS_SHOWN_AT_ONCE_DEFAULT
  );
  return {
    shell: normalized,
    imagePosition: isVertical ? "top" : "left",
    density: isVertical ? "compact" : "standard",
    itemsPerRow,
    ...(isVertical ? { showAsGrid } : {}),
    ...(isCheckboxStack ? { productsShownAtOnce } : {}),
    ...(isFbtBundle
      ? {
          includeCurrentPageProduct:
            existingLayout?.includeCurrentPageProduct === true,
          // Default ON (legacy FBT): first item pre-selected and not unselectable.
          lockFirstProductSelection:
            existingLayout?.lockFirstProductSelection !== false,
        }
      : {}),
  };
}

export const PRODUCT_PAGE_UPSELL_LAYOUT = {
  heading: "You may also like",
  defaultShell: PRODUCT_SHELL_LAYOUT.CART_DRAWER_HORIZONTAL,
};

export const PRODUCT_PAGE_UPSELL_FBT_HEADING = "Frequently bought together";
export const PRODUCT_PAGE_UPSELL_CHECKBOX_STACK_HEADING = "Product Add-Ons";

export function resolveProductPageUpsellHeadingForShell(shell) {
  const normalized =
    shell === PRODUCT_SHELL_LAYOUT.HORIZONTAL
      ? PRODUCT_SHELL_LAYOUT.CART_DRAWER_HORIZONTAL
      : shell;
  if (isCartDrawerFbtBundleShell(normalized)) {
    return PRODUCT_PAGE_UPSELL_FBT_HEADING;
  }
  if (isCartDrawerCheckboxStackShell(normalized)) {
    return PRODUCT_PAGE_UPSELL_CHECKBOX_STACK_HEADING;
  }
  return PRODUCT_PAGE_UPSELL_LAYOUT.heading;
}

export const PRODUCT_PAGE_UPSELL_POPUP_SETTINGS = {
  zIndex: 1,
  customCSS: {
    desktop: {
      width: "100%",
      height: "auto",
      widthUnit: "%",
      heightUnit: "auto",
      borderRadius: "0px",
      backgroundColor: "transparent",
    },
    mobile: {
      width: "100%",
      height: "auto",
      widthUnit: "%",
      heightUnit: "auto",
      borderRadius: "0px",
      backgroundColor: "transparent",
    },
  },
  dynamicalOptions: {
    overlay_style: "None",
    isHideBgOverlay: true,
    fontFamily: "Inter",
    popupPosition: {
      desktop: "center center",
      mobile: "center center",
    },
    window: {
      padding: {
        desktop: 0,
        mobile: 0,
      },
    },
  },
};

const DEFAULT_PRODUCT_CONDITION = {
  allowTrigger: true,
  oneTimeShow: false,
  linkToProduct: true,
  linkToProductOpenInNewTab: true,
  offer_is_triggered: { type: "all_product", value: true },
  products_to_offer: { type: "shopify_recommended", value: [] },
  offerRules: [],
  allowMultipleProducts: true,
  products_to_offer_limit: 3,
};

function baseHeadingCss() {
  return {
    desktop: {
      fontFamily: "Inter, Segoe UI, system-ui, sans-serif",
      fontSize: "15px",
      fontWeight: "700",
      color: "#0f172a",
      justifyContent: "flex-start",
      textAlign: "left",
      marginBottom: "10px",
    },
    mobile: {
      fontFamily: "Inter, Segoe UI, system-ui, sans-serif",
      fontSize: "14px",
      fontWeight: "700",
      color: "#0f172a",
      justifyContent: "flex-start",
      textAlign: "left",
      marginBottom: "8px",
    },
  };
}

export function buildStaticProductPageUpsellHeadingSection(
  text = PRODUCT_PAGE_UPSELL_LAYOUT.heading
) {
  return {
    type: "TITLE",
    text,
    setting: {
      productPagePart: "upsell_heading",
      customCSS: baseHeadingCss(),
      overrideCss: { desktop: "", mobile: "" },
    },
  };
}

export function buildStaticProductPageUpsellProductSection(
  shell = PRODUCT_SHELL_LAYOUT.CART_DRAWER_HORIZONTAL
) {
  const isVertical = shell === PRODUCT_SHELL_LAYOUT.CART_DRAWER_VERTICAL;
  const isCheckboxStack = shell === PRODUCT_SHELL_LAYOUT.CART_DRAWER_CHECKBOX_STACK;
  const isFbtBundle = shell === PRODUCT_SHELL_LAYOUT.CART_DRAWER_FBT_BUNDLE;
  return {
    type: "PRODUCT",
    setting: {
      productPagePart: "upsell_product",
      layout: {
        shell,
        imagePosition: isVertical ? "top" : "left",
        density: isVertical ? "compact" : "standard",
        itemsPerRow: CART_DRAWER_UPSELL_ITEMS_PER_ROW_DEFAULT,
        ...(isCheckboxStack
          ? {
              productsShownAtOnce:
                PRODUCT_PAGE_UPSELL_PRODUCTS_SHOWN_AT_ONCE_FBT_DEFAULT,
            }
          : {}),
      },
    },
    sections: isVertical
      ? buildProductPageUpsellVerticalProductSubsections()
      : isFbtBundle
        ? buildProductPageUpsellSubsectionsForShell(shell)
        : buildProductPageUpsellHorizontalProductSubsections(),
    products: [],
  };
}

export function buildStaticProductPageUpsellBrandSection() {
  const section = buildProductRecommendationBrandAttributionSection();
  return {
    ...section,
    setting: {
      ...section.setting,
      productPagePart: "brand_attribution",
      customCSS: {
        desktop: {
          fontSize: "11px",
          fontWeight: "400",
          color: "#64748b",
          justifyContent: "center",
          padding: "8px 0 0",
          textAlign: "center",
        },
        mobile: {
          fontSize: "10px",
          color: "#64748b",
          padding: "6px 0 0",
          textAlign: "center",
        },
      },
    },
  };
}

/** Editor + preview sections: heading → product slider → WeUpsell branding. */
export function buildStaticProductPageUpsellSections(
  layout = PRODUCT_PAGE_UPSELL_LAYOUT
) {
  return [
    buildStaticProductPageUpsellHeadingSection(layout.heading),
    buildStaticProductPageUpsellProductSection(
      layout.defaultShell || PRODUCT_SHELL_LAYOUT.CART_DRAWER_HORIZONTAL
    ),
    buildStaticProductPageUpsellBrandSection(),
  ];
}

function mergeLayout(overrides = {}) {
  return {
    ...PRODUCT_PAGE_UPSELL_LAYOUT,
    ...(overrides || {}),
  };
}

function mergePopupSettings(stepSettings = {}) {
  const baseWindow = PRODUCT_PAGE_UPSELL_POPUP_SETTINGS.dynamicalOptions?.window || {};
  const stepWindow = stepSettings.dynamicalOptions?.window || {};

  return {
    ...PRODUCT_PAGE_UPSELL_POPUP_SETTINGS,
    ...stepSettings,
    customCSS: {
      desktop: {
        ...PRODUCT_PAGE_UPSELL_POPUP_SETTINGS.customCSS.desktop,
        ...(stepSettings.customCSS?.desktop || {}),
      },
      mobile: {
        ...PRODUCT_PAGE_UPSELL_POPUP_SETTINGS.customCSS.mobile,
        ...(stepSettings.customCSS?.mobile || {}),
      },
    },
    dynamicalOptions: {
      ...PRODUCT_PAGE_UPSELL_POPUP_SETTINGS.dynamicalOptions,
      ...(stepSettings.dynamicalOptions || {}),
      window: {
        ...baseWindow,
        ...stepWindow,
        padding: {
          ...baseWindow.padding,
          ...(stepWindow.padding || {}),
        },
      },
    },
  };
}

export function syncProductPageUpsellLayoutFromSections(step, layoutBase = PRODUCT_PAGE_UPSELL_LAYOUT) {
  const sections = step?.section ?? step?.sections ?? [];
  const layout = mergeLayout(step?.productPageUpsellLayout || layoutBase);
  const headingSection =
    sections[STATIC_PRODUCT_PAGE_UPSELL_SECTION_INDEX.UPSELL_HEADING];
  const productSection =
    sections[STATIC_PRODUCT_PAGE_UPSELL_SECTION_INDEX.UPSELL_PRODUCT];

  if (headingSection?.text) {
    layout.heading = headingSection.text;
  }
  const shell = productSection?.setting?.layout?.shell;
  if (shell) {
    layout.defaultShell = shell;
  }
  return layout;
}

export function syncStaticProductPageUpsellSchemaStep(step) {
  if (!step || typeof step !== "object") {
    return step;
  }

  const sections = step.section ?? step.sections ?? [];
  const productPageUpsellLayout = syncProductPageUpsellLayoutFromSections({
    ...step,
    section: sections,
  });

  return {
    ...step,
    productPageUpsellLayout,
    popupSettings: {
      ...mergePopupSettings(step.popupSettings),
      step_name: step.popupSettings?.step_name || "Product page",
    },
    section: sections,
  };
}

export function ensureStaticProductPageUpsellSchemaStep(step) {
  if (!step || typeof step !== "object") {
    return {
      section: buildStaticProductPageUpsellSections(),
      productPageUpsellLayout: PRODUCT_PAGE_UPSELL_LAYOUT,
      popupSettings: mergePopupSettings(),
    };
  }

  const layout = mergeLayout(step.productPageUpsellLayout);
  const defaults = buildStaticProductPageUpsellSections(layout);
  const raw = step.section ?? step.sections ?? [];
  const sections = Array.from({ length: STATIC_PRODUCT_PAGE_UPSELL_SECTION_COUNT }, (_, index) => {
    const apiSection = raw[index];
    const defaultSection = defaults[index];
    if (!defaultSection) return apiSection;
    if (!apiSection) return defaultSection;
    return {
      ...defaultSection,
      ...apiSection,
      type: apiSection.type || defaultSection.type,
      text:
        apiSection.text != null && String(apiSection.text).trim() !== ""
          ? apiSection.text
          : defaultSection.text,
      setting: {
        ...defaultSection.setting,
        ...apiSection.setting,
        productPagePart:
          apiSection.setting?.productPagePart || defaultSection.setting?.productPagePart,
      },
      ...(Array.isArray(apiSection.sections) && apiSection.sections.length
        ? { sections: apiSection.sections }
        : { sections: defaultSection.sections }),
      ...(Array.isArray(apiSection.products) ? { products: apiSection.products } : {}),
    };
  });

  return syncStaticProductPageUpsellSchemaStep({
    ...step,
    productPageUpsellLayout: syncProductPageUpsellLayoutFromSections({
      ...step,
      section: sections,
      productPageUpsellLayout: layout,
    }),
    section: sections,
  });
}

export function ensureStaticProductPageUpsellPopup(popup) {
  if (!popup || !isStaticProductPageUpsell(popup)) {
    return popup;
  }

  const productCondition = popup.generalSettings?.condition?.product || {};
  const mergedProductCondition = {
    ...DEFAULT_PRODUCT_CONDITION,
    ...productCondition,
    products_to_offer: {
      type: "shopify_recommended",
      value: [],
      ...(productCondition.products_to_offer || {}),
    },
    allowMultipleProducts:
      productCondition.allowMultipleProducts ?? true,
    products_to_offer_limit:
      Number.isFinite(Number(productCondition.products_to_offer_limit)) &&
      Number(productCondition.products_to_offer_limit) >= 1
        ? Number(productCondition.products_to_offer_limit)
        : 3,
  };

  return {
    ...popup,
    popUpType: popup.popUpType || PRODUCT_PAGE_UPSELL_TEMPLATE_TYPE,
    generalSettings: {
      ...popup.generalSettings,
      hasProduct: popup.generalSettings?.hasProduct ?? true,
      isStaticProductPageUpsell: true,
      isDraggable: false,
      lockDraggable: popup.generalSettings?.lockDraggable ?? true,
      popupLayoutId:
        popup.generalSettings?.popupLayoutId || POPUP_LAYOUT_IDS.PRODUCT_PAGE_UPSELL,
      staticProductPageUpsellSelectedPart:
        popup.generalSettings?.staticProductPageUpsellSelectedPart ??
        STATIC_PRODUCT_PAGE_UPSELL_SECTION_INDEX.UPSELL_HEADING,
      cartDrawerUpsellShell:
        popup.generalSettings?.cartDrawerUpsellShell ||
        PRODUCT_SHELL_LAYOUT.CART_DRAWER_HORIZONTAL,
      condition: {
        ...(popup.generalSettings?.condition || {}),
        product: mergedProductCondition,
        embed: {
          ...buildDefaultProductPageSlotEmbed("1"),
          ...(popup.generalSettings?.condition?.embed || {}),
        },
      },
    },
  };
}

export function normalizeStaticProductPageUpsellPopup(popup) {
  if (!popup) return popup;

  const typedAsProductPageUpsell =
    (popup.popUpType || popup.template_type) === PRODUCT_PAGE_UPSELL_TEMPLATE_TYPE ||
    isPopupLayout(popup, POPUP_LAYOUT_IDS.PRODUCT_PAGE_UPSELL) ||
    popup.generalSettings?.isStaticProductPageUpsell === true;

  if (!typedAsProductPageUpsell && !isStaticProductPageUpsell(popup)) {
    return popup;
  }

  const schema = Array.isArray(popup.schema)
    ? popup.schema.map((step) =>
        isStaticProductPageUpsellContentStep(step)
          ? syncStaticProductPageUpsellSchemaStep(step)
          : step
      )
    : popup.schema;

  let nextSchema = schema;
  if (
    typedAsProductPageUpsell &&
    (!Array.isArray(schema) || schema.length === 0)
  ) {
    nextSchema = [
      ensureStaticProductPageUpsellSchemaStep({
        productPageUpsellLayout: PRODUCT_PAGE_UPSELL_LAYOUT,
        section: buildStaticProductPageUpsellSections(),
      }),
    ];
  }

  return ensureStaticProductPageUpsellPopup({
    ...popup,
    popUpType: popup.popUpType || PRODUCT_PAGE_UPSELL_TEMPLATE_TYPE,
    schema: nextSchema,
  });
}

export function isStaticProductPageUpsell(popup) {
  if (!popup) return false;
  if (popup.generalSettings?.isStaticProductPageUpsell === true) return true;
  if (isPopupLayout(popup, POPUP_LAYOUT_IDS.PRODUCT_PAGE_UPSELL)) return true;

  const type = popup.popUpType || popup.template_type;
  if (type !== PRODUCT_PAGE_UPSELL_TEMPLATE_TYPE) return false;

  const schema = Array.isArray(popup?.schema) ? popup.schema : [];
  return schema.some((step) => isStaticProductPageUpsellContentStep(step));
}

export function getStaticProductPageUpsellSections(popup, stepIndex = 0) {
  const step = Array.isArray(popup?.schema) ? popup.schema[stepIndex] : null;
  if (!step) return [];
  return step.section ?? step.sections ?? [];
}

export function buildProductPageUpsellTemplate(overrides = {}) {
  const layout = mergeLayout(overrides.productPageUpsellLayout);
  const sections = buildStaticProductPageUpsellSections(layout);

  return {
    popUpType: PRODUCT_PAGE_UPSELL_TEMPLATE_TYPE,
    is_active: true,
    ...overrides,
    generalSettings: {
      isDraggable: false,
      isStaticProductPageUpsell: true,
      hasProduct: true,
      title: "Product Page Upsell",
      staticProductPageUpsellSelectedPart:
        STATIC_PRODUCT_PAGE_UPSELL_SECTION_INDEX.UPSELL_HEADING,
      popupLayoutId: POPUP_LAYOUT_IDS.PRODUCT_PAGE_UPSELL,
      cartDrawerUpsellShell: layout.defaultShell,
      condition: {
        trigger: { step: 0 },
        embed: buildDefaultProductPageSlotEmbed("1"),
        product: {
          ...DEFAULT_PRODUCT_CONDITION,
          ...(overrides.generalSettings?.condition?.product || {}),
        },
        ...(overrides.generalSettings?.condition || {}),
      },
      ...(overrides.generalSettings || {}),
      isStaticProductPageUpsell: true,
      isDraggable: false,
    },
    schema: [
      {
        activeStep: 0,
        productPageUpsellLayout: layout,
        popupSettings: {
          ...PRODUCT_PAGE_UPSELL_POPUP_SETTINGS,
          step_name: "Product page",
          ...(overrides.schema?.[0]?.popupSettings || {}),
        },
        section: sections,
      },
    ],
  };
}

/** Canonical default_schema for Mongo seed / exports. */
export function buildProductPageUpsellDefaultSchema() {
  const contentStep = ensureStaticProductPageUpsellSchemaStep({
    productPageUpsellLayout: PRODUCT_PAGE_UPSELL_LAYOUT,
    section: buildStaticProductPageUpsellSections(),
  });

  return ensureStaticProductPageUpsellPopup({
    popUpType: PRODUCT_PAGE_UPSELL_TEMPLATE_TYPE,
    generalSettings: {
      title: "Product Page Upsell",
      isStaticProductPageUpsell: true,
      isDraggable: false,
      lockDraggable: true,
      popupLayoutId: POPUP_LAYOUT_IDS.PRODUCT_PAGE_UPSELL,
      staticProductPageUpsellSelectedPart:
        STATIC_PRODUCT_PAGE_UPSELL_SECTION_INDEX.UPSELL_HEADING,
      condition: {
        product: {
          products_to_offer: { type: "shopify_recommended", value: [] },
          allowMultipleProducts: true,
          products_to_offer_limit: 3,
        },
        embed: buildDefaultProductPageSlotEmbed("1"),
      },
    },
    schema: [contentStep],
  });
}
